import { makeObservable, observable } from 'mobx'

import {
  BicCompletionMode,
  BlockTypeEnum,
  IBookingDeadlineOptions,
  IDefaultPermitType,
  IEndClosureAction,
  IInspectionOptions,
  IPermitType,
  IPermitTypeField,
  IRecurringOptions,
  IStartClosureAction,
  IWorkflowDurationOptions,
  IWorkflowStep,
  PermitFieldType,
  WorkflowDurationType,
  WorkflowDurationUnit,
  WorkflowStepType,
} from '~/client/graph/types.generated'

import { WorkflowTypeCreatedByEnum } from '../enums/WorkflowTypeCreatedBy'
import Localization from '../localization/LocalizationManager'
import WorkflowCategoriesStore from '../stores/domain/WorkflowCategories.store'
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  MINUTES_IN_MONTH,
  MINUTES_IN_WEEK,
} from '../stores/ui/ProjectDate.store'
import { NO_DEADLINE_OPTION } from '../utils/DeadlineOptions'
import { EMPTY_OBJECT_ID } from '../utils/GraphExecutor'
import { NO_VALUE } from '../utils/usefulStrings'
import { copyObjectDeep } from '../utils/util'
import BaseModel from './BaseModel'

export enum DefaultWorkflowType {
  // Commented out, not removed: the backend still defines these, but nothing in
  // the client names them since the konva pin stopped switching on workflow type.
  // knip fails the push on a member nothing reaches, so uncomment when you need one.
  // Hotwork = 'hotwork',
  // Ladder = 'ladder',
  // EnergizedRoom = 'energized',
  // Aerial = 'aerial',
  // FireExtinguisherInspection = 'fireExtinguisher',
  // SurfacePenetration = 'surfacePenetration',
  // InteriorConcretePenetration = 'interiorConcretePenetration',
  // OffRoadHeavyEquipment = 'offRoadHeavyEquipment',
  EmissionForm = 'emission',
  MaterialTransfer = 'materialTransfer',
  Announcement = 'announcement',
  Closure = 'closure',
  // Delivery = 'delivery',
  Activity = 'activity',
}

export enum InstructionListType {
  Enumerated = 'enumerated',
  Bulleted = 'bulleted',
  None = 'none',
}

export function getInstructionListTypeTranslate(value: InstructionListType) {
  switch (value) {
    case InstructionListType.Bulleted:
      return Localization.translator.instructionListTypes.bulleted
    case InstructionListType.Enumerated:
      return Localization.translator.instructionListTypes.enumerated
    case InstructionListType.None:
      return Localization.translator.instructionListTypes.none
    default:
      throw new Error(value + ' is unhandled')
  }
}

export type IWorkflowTypeDto = IPermitType
export type IDefaultWorkflowTypeDto = IDefaultPermitType
export type IWorkflowTypeField = IPermitTypeField
export type IWorkflowTypeAction = IStartClosureAction | IEndClosureAction

/** How a step's ball-in-court sign-offs complete it. Steps saved before the mode existed only have the flag. */
export function getBicCompletionMode(step: IWorkflowStep): BicCompletionMode {
  if (step?.bicCompletionMode) {
    return step.bicCompletionMode
  }

  return step?.requireAllSubmissions
    ? BicCompletionMode.AllUsers
    : BicCompletionMode.AnyOne
}

/** What a step's ball-in-court rules assign once they resolve on a workflow. */
export type BicAssignees = 'users' | 'companies' | 'usersAndCompanies'

/**
 * Who completes a step in the given mode, as its ball-in-court rules name it. An all-users step
 * is worded for the users, companies or both its rules assign.
 */
export function getBicCompletionModeLabel(
  mode: BicCompletionMode,
  assignees: BicAssignees = 'users',
): string {
  switch (mode) {
    case BicCompletionMode.AllUsers:
      if (assignees === 'companies') {
        return Localization.translator.allAssignedCompanies
      }
      if (assignees === 'usersAndCompanies') {
        return Localization.translator.allAssignedUsersAndCompanies
      }
      return Localization.translator.allAssignedUsers
    case BicCompletionMode.OnePerRule:
      return Localization.translator.oneUserPerRule
    default:
      return Localization.translator.anyAssignedUser
  }
}

const CUSTOM_FORM_TYPE_INDICATOR = '_'
const SMART_IMPORT_TYPE = 'smart-import'
const ADMIN_ONLY_TYPES = [
  DefaultWorkflowType.Announcement,
  DefaultWorkflowType.Closure,
]

export default class WorkflowType
  extends BaseModel<IWorkflowTypeDto>
  implements IWorkflowTypeDto
{
  public static fromDefault(dto: IDefaultWorkflowTypeDto): WorkflowType {
    return new WorkflowType(
      dto.id,
      dto.type,
      dto.name,
      null,
      dto.isEnabled,
      null,
      dto.orderIndex,
      dto.workflowSteps,
      dto.isAutoActivationEnabled,
      dto.isWorkflowTitleMandatory,
      dto.inspectionOptions,
      dto.bookingDeadlineOptions,
      dto.autoEndStepIds,
      null,
      false,
      dto.categoryId,
      dto.maxDurationOptions,
      dto.defaultDurationOptions,
      dto.recurringOptions,
      dto.iconName,
      dto.createdAt,
      dto.updatedAt,
    )
  }

  public static fromDto(dto: IWorkflowTypeDto): WorkflowType {
    return new WorkflowType(
      dto.id,
      dto.type,
      dto.name,
      dto.projectId,
      dto.isEnabled,
      dto.basedOn,
      dto.orderIndex,
      dto.workflowSteps,
      dto.isAutoActivationEnabled,
      dto.isWorkflowTitleMandatory,
      dto.inspectionOptions,
      dto.bookingDeadlineOptions,
      dto.autoEndStepIds,
      dto.shouldBlockOnNonWorkTimes,
      dto.isDeleted,
      dto.categoryId,
      dto.maxDurationOptions,
      dto.defaultDurationOptions,
      dto.recurringOptions,
      dto.iconName,
      dto.createdAt,
      dto.updatedAt,
    )
  }

  @observable public type: string
  @observable public name: string
  @observable public projectId: string
  @observable public categoryId?: string
  @observable public isEnabled: boolean
  @observable public basedOn: string
  @observable public orderIndex: number
  @observable public workflowSteps: IWorkflowStep[]
  @observable public isAutoActivationEnabled: boolean
  @observable public isWorkflowTitleMandatory: boolean
  @observable public inspectionOptions?: IInspectionOptions
  @observable public bookingDeadlineOptions?: IBookingDeadlineOptions
  @observable public autoEndStepIds?: string[]
  @observable public shouldBlockOnNonWorkTimes: boolean
  @observable public isDeleted?: boolean
  @observable public createdAt: number
  @observable public updatedAt: number
  @observable public maxDurationOptions: IWorkflowDurationOptions
  @observable public defaultDurationOptions: IWorkflowDurationOptions
  @observable public recurringOptions?: IRecurringOptions
  @observable public iconName?: string

  public constructor(
    id: string,
    type: string,
    name: string,
    projectId: string,
    isEnabled: boolean,
    basedOn: string,
    orderIndex: number,
    workflowSteps: IWorkflowStep[],
    isAutoActivationEnabled: boolean,
    isWorkflowTitleMandatory: boolean,
    inspectionOptions?: IInspectionOptions,
    bookingDeadlineOptions?: IBookingDeadlineOptions,
    autoEndStepIds?: string[],
    shouldBlockOnNonWorkTimes?: boolean,
    isDeleted?: boolean,
    categoryId?: string,
    maxDurationOptions?: IWorkflowDurationOptions,
    defaultDurationOptions?: IWorkflowDurationOptions,
    recurringOptions?: IRecurringOptions,
    iconName?: string,
    createdAt: number = 0,
    updatedAt: number = 0,
  ) {
    super(id)
    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)

    this.type = type
    this.name = name
    this.projectId = projectId
    this.categoryId = categoryId
    this.isEnabled = isEnabled
    this.basedOn = basedOn
    this.orderIndex = orderIndex || 0
    this.workflowSteps = workflowSteps || []
    this.isAutoActivationEnabled = isAutoActivationEnabled
    this.isWorkflowTitleMandatory = isWorkflowTitleMandatory
    this.inspectionOptions = inspectionOptions
    this.bookingDeadlineOptions = bookingDeadlineOptions
    this.autoEndStepIds = autoEndStepIds || []
    this.shouldBlockOnNonWorkTimes = shouldBlockOnNonWorkTimes || false
    this.isDeleted = isDeleted
    this.defaultDurationOptions = defaultDurationOptions || {
      isEnabled: false,
      durationType: WorkflowDurationType.Time,
      durationUnit: WorkflowDurationUnit.Minutes,
      duration: 0,
    }
    this.maxDurationOptions = maxDurationOptions || {
      isEnabled: false,
      durationType: WorkflowDurationType.Time,
      durationUnit: WorkflowDurationUnit.Minutes,
      duration: 0,
    }
    this.recurringOptions = recurringOptions || {
      isEnabled: false,
      maxDuration: 0,
    }
    this.iconName = iconName

    this.createdAt = createdAt || 0
    this.updatedAt = updatedAt || 0

    makeObservable(this)
  }

  // available within all Projects per Tenant
  public get isDefault(): boolean {
    return !this.projectId
  }

  // No persisted server id yet — a brand-new/unsaved type (e.g. a smart-import
  // draft) that hasn't been saved.
  public get isNew(): boolean {
    return !this.id
  }

  public get isInspectionType(): boolean {
    return !!this.inspectionOptions
  }

  public get inspectionFrequency(): number {
    if (!this.isInspectionType) {
      return 0
    }

    return this.inspectionOptions.inspectionFrequency
  }

  public get hasStartStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Start)
  }

  public get hasApprovalStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Approval)
  }

  public get isApprovalLast(): boolean {
    return this.lastStep?.type === WorkflowStepType.Approval
  }

  public get isEmissionWorkflowType(): boolean {
    return this.type.includes(DefaultWorkflowType.EmissionForm)
  }

  public get isEmissionFormType(): boolean {
    return this.isEmissionWorkflowType
  }

  public get isMaterialTransfer(): boolean {
    return this.type === DefaultWorkflowType.MaterialTransfer
  }

  public get isActivity(): boolean {
    return this.type === DefaultWorkflowType.Activity
  }

  public get isNonCustomizable(): boolean {
    return this.isMaterialTransfer || this.isActivity
  }

  // Admin-only types are offered for creation only to project admins (see
  // WorkflowTypesStore.enabledTypes). Frontend gating, not server-enforced.
  public get isAdminOnly(): boolean {
    return ADMIN_ONLY_TYPES.some(adminOnlyType =>
      this.type.startsWith(adminOnlyType),
    )
  }

  // Creation is toggled off in the Workflow Configurator. Still a valid stored
  // selection — it just can't be created — so surfaces render it muted rather
  // than dropping it. Narrower than WorkflowTypesStore.enabledTypes, which also
  // hides admin-only types from members.
  public get isInactive(): boolean {
    return !this.isEnabled
  }

  public get isSmartImport(): boolean {
    return this.type.startsWith(SMART_IMPORT_TYPE)
  }

  public get isCustom(): boolean {
    return this.type.includes(CUSTOM_FORM_TYPE_INDICATOR)
  }

  public get isDeletable(): boolean {
    if (this.isNonCustomizable) return false
    return this.isCustom || this.isSmartImport
  }
  public get initialStep(): IWorkflowStep {
    return this.workflowSteps[0]
  }

  public get lastStep(): IWorkflowStep {
    return this.workflowSteps[this.workflowSteps.length - 1]
  }

  public get stepsWithoutRecurring(): IWorkflowStep[] {
    return this.workflowSteps.filter(
      s => s.type !== WorkflowStepType.RecurringInspection,
    )
  }

  public get recurringInspectionStep(): IWorkflowStep {
    return this.workflowSteps.find(
      s => s.type === WorkflowStepType.RecurringInspection,
    )
  }

  public get locationField(): IWorkflowTypeField {
    return this.initialStep?.fields?.find(
      field => field?.type === PermitFieldType.Location,
    )
  }

  public get equipmentField(): IWorkflowTypeField {
    return this.initialStep?.fields?.find(
      field => field?.type === PermitFieldType.Equipment,
    )
  }

  public get allLocationFields(): IWorkflowTypeField[] {
    return this.allWorkflowFieldsFlat.filter(
      f =>
        f.type === PermitFieldType.Location ||
        f.type === PermitFieldType.LocationInformational,
    )
  }

  public get allEquipmentFields(): IWorkflowTypeField[] {
    return this.allWorkflowFieldsFlat.filter(
      f =>
        f.type === PermitFieldType.Equipment ||
        f.type === PermitFieldType.EquipmentInformational,
    )
  }

  // Walks every schema-level field on the workflow type: the flat
  // `step.fields` list AND every value inside `step.conditionalFields`
  // (table columns and conditional/dependent children alike). The
  // `IWorkflowTypeField` schema is flat — there is no recursive children
  // prop — so depth-1 traversal is sufficient.
  private get allWorkflowFieldsFlat(): IWorkflowTypeField[] {
    return this.workflowSteps.flatMap(s => [
      ...(s.fields ?? []),
      ...(s.conditionalFields ?? []).flatMap(cf => cf?.values ?? []),
    ])
  }

  public get hasBookingDeadline(): boolean {
    return (
      !!this.bookingDeadlineOptions?.blockType &&
      !!this.bookingDeadlineOptions?.deadlineInterval
    )
  }

  public get bookingDeadlineInterval(): number {
    return this.bookingDeadlineOptions?.deadlineInterval || NO_DEADLINE_OPTION
  }

  public get isDeadlineHidden(): boolean {
    if (!this.hasBookingDeadline) return true

    return (
      this.bookingDeadlineOptions.blockType === BlockTypeEnum.Hide ||
      this.bookingDeadlineOptions.deadlineInterval === NO_DEADLINE_OPTION
    )
  }

  public get shouldBlockBooking(): boolean {
    if (!this.hasBookingDeadline) return false

    return this.bookingDeadlineOptions.blockType === BlockTypeEnum.Block
  }

  public get isAutoEndingEnabled(): boolean {
    return !!this.autoEndStepIds.length
  }

  public get canActivateAutoEnd(): boolean {
    return this.stepsWithoutRecurring.length > 1
  }

  public get autoEndStepId(): string {
    return this.isAutoEndingEnabled && this.autoEndStepIds[0]
  }

  public get createdBy(): WorkflowTypeCreatedByEnum {
    return this.isCustom || this.isSmartImport
      ? WorkflowTypeCreatedByEnum.PROJECT
      : WorkflowTypeCreatedByEnum.DEFAULT
  }

  public get maxDurationInMinutes(): number {
    return this.durationToMinutes(this.maxDurationOptions)
  }

  public get defaultDurationInMinutes(): number {
    if (
      !this.defaultDurationOptions ||
      !this.defaultDurationOptions.isEnabled
    ) {
      return 0
    }
    return this.durationToMinutes(this.defaultDurationOptions)
  }

  private durationToMinutes(durationOptions: IWorkflowDurationOptions): number {
    if (durationOptions.durationType !== WorkflowDurationType.Time) {
      return
    }

    switch (durationOptions.durationUnit) {
      case WorkflowDurationUnit.Minutes:
        return durationOptions.duration
      case WorkflowDurationUnit.Hours:
        return durationOptions.duration * MINUTES_IN_HOUR
      case WorkflowDurationUnit.Days:
        return durationOptions.duration * MINUTES_IN_DAY
      case WorkflowDurationUnit.Weeks:
        return durationOptions.duration * MINUTES_IN_WEEK
      case WorkflowDurationUnit.Months:
        return durationOptions.duration * MINUTES_IN_MONTH
    }
  }

  public isLastStep = (workflowStepId: string): boolean => {
    return this.lastStep?.id === workflowStepId
  }

  public getStepRuleIds = (workflowStepId: string): string[] => {
    return (
      this.workflowSteps.find(s => s.id === workflowStepId)?.workflowRuleIds ||
      []
    )
  }

  /**
   * Whether every signer of the step keeps their own copy of its fields. True in both
   * "All users must complete" and one-sign-off-per-rule mode: the latter keeps the flag set so
   * older apps, which don't know the mode, fall back to requiring everyone.
   */
  public getStepRequireAllSubmissions = (workflowStepId: string): boolean => {
    const step = this.workflowSteps.find(s => s.id === workflowStepId)
    return step?.requireAllSubmissions ?? false
  }

  public getStepCompletionMode = (
    workflowStepId: string,
  ): BicCompletionMode => {
    const step = this.workflowSteps.find(s => s.id === workflowStepId)
    return getBicCompletionMode(step)
  }

  public getFormattedUpdatedAt = (
    formatDateFn: (date: Date | number) => string,
  ): string => {
    return !this.isDefault && this.updatedAt
      ? formatDateFn(this.updatedAt)
      : NO_VALUE
  }

  public getCategoryName = (
    categoriesStore: WorkflowCategoriesStore,
    includeDeleted: boolean = false,
  ): string => {
    return categoriesStore.getNameById(this.categoryId, includeDeleted)
  }

  public getDeepCopy(): WorkflowType {
    const dtoCopy = copyObjectDeep<IWorkflowTypeDto>(this)
    return WorkflowType.fromDto(dtoCopy)
  }

  public getDto(): WorkflowType {
    const dto = this.getDeepCopy()
    /**
     * * Since default workflow types don't have a projectId
     * * we need to set it as EMPTY_OBJECT_ID before saving, because
     * * IWorkflowTypeDtoInput doesn't allow null or empty values
     */
    if (this.isDefault) {
      dto.projectId = EMPTY_OBJECT_ID
    }

    dto.isDeleted = undefined

    dto.workflowSteps.forEach(step => {
      step.actions = undefined
      if (!step.notificationUserIds) {
        step.notificationUserIds = []
      }
    })

    return dto
  }
}
